import { FC, useContext, useEffect, useMemo } from 'react';
import { Edit as EditIcon } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { isEmpty, toString } from 'lodash';
import { companySummaryUrl } from 'common/config/urls';
import { useStore } from 'common/store';
import { LayoutContextValues, PageBreadcrumb, UseStoreValues } from 'common/types/store';
import { ReadyForAuditBadge } from 'components/ReadyForAuditBadge';
import LayoutContext from 'context/LayoutContext';
import { FUND_INVESTMENTS_SLUG } from 'pages/FundInvestments/common/constants';
import { getStringValue } from 'utilities';
import { BreadcrumbEditButtonProps, UseCompanyBreadcrumbParams } from './types';

const VALUATION_PAGE = 'Valuations';
const FIRM_OWNED_PAGES = [VALUATION_PAGE, FUND_INVESTMENTS_SLUG];

const BreadcrumbEditButton: FC<BreadcrumbEditButtonProps> = props => {
  const { handleOnClick } = props;

  return (
    <Tooltip arrow title="Edit">
      <IconButton className="edit-icon" id="edit-handler-btn" onClick={handleOnClick} size="small">
        <EditIcon aria-label="edit" color="primary" style={{ fontSize: '1.2rem' }} />
      </IconButton>
    </Tooltip>
  );
};

const getPageTitleForFirstBreadcrumb = (
  page: string | undefined,
  firmOwned: boolean | undefined,
  firmName: string | undefined,
  companyName: string | undefined
) => {
  if (FIRM_OWNED_PAGES.includes(toString(page))) {
    return firmOwned ? firmName : 'Scalar Valuation';
  }
  return companyName;
};

const useCompanyBreadcrumb = (params: UseCompanyBreadcrumbParams) => {
  const { customAction, item, page, title = page } = params;

  const [storeValues] = useStore() as unknown as UseStoreValues;
  const { companyInfo, firmInfo, valuationInfo } = storeValues;
  const { is_firm_owned: firmOwned, is_ready_for_audit: isValuationReadyForAudit } = valuationInfo ?? {};
  const { name: firmName } = firmInfo ?? {};

  const { setPageBreadcrumbs, togglePageForm, viewOnlyUser } = useContext(
    LayoutContext
  ) as unknown as LayoutContextValues;

  const companyURL = useMemo(
    () => companySummaryUrl(getStringValue(firmInfo?.slug), getStringValue(companyInfo?.slug)),
    [companyInfo, firmInfo]
  );

  const versionName = useMemo(() => getStringValue(item?.label), [item]);
  const versionStatus = useMemo(() => item?.status ?? null, [item]);
  const allocationVersion = useMemo(() => item?.allocationVersion ?? null, [item]);
  const handleOnClickEditButton = useMemo(() => customAction ?? togglePageForm, [customAction, togglePageForm]);

  const companyBreadcrumbs = useMemo(() => {
    if (isEmpty(companyInfo)) {
      return [];
    }

    const breadcrumbs = [
      {
        title: getPageTitleForFirstBreadcrumb(page, firmOwned, firmName, companyInfo.name),
        ...(FIRM_OWNED_PAGES.includes(toString(page)) && { isScalarOwned: !firmOwned }),
        ...(!FIRM_OWNED_PAGES.includes(toString(page)) && { url: companyURL }),
      },
      {
        title,
      },
    ] as PageBreadcrumb[];

    if (versionName) {
      // Only show version status
      if (isEmpty(allocationVersion)) {
        breadcrumbs.push({
          title: versionName,
          icon: (
            <>
              {versionStatus}
              {isValuationReadyForAudit && !viewOnlyUser && <ReadyForAuditBadge />}
              <BreadcrumbEditButton handleOnClick={handleOnClickEditButton} />
            </>
          ),
        });
      } else {
        // Show version status and allocation version status
        const { label: allocationVersionName, status: AllocationVersionStatus } = allocationVersion;

        breadcrumbs.push(
          ...[
            {
              title: versionName,
            },
            {
              title: allocationVersionName,
              icon: (
                <>
                  {AllocationVersionStatus}
                  <BreadcrumbEditButton handleOnClick={handleOnClickEditButton} />
                </>
              ),
            },
          ]
        );
      }
    }

    return breadcrumbs;
  }, [
    companyInfo,
    page,
    firmOwned,
    firmName,
    companyURL,
    title,
    versionName,
    allocationVersion,
    versionStatus,
    isValuationReadyForAudit,
    viewOnlyUser,
    handleOnClickEditButton,
  ]);

  // Set Company breadcrumbs
  useEffect(() => {
    setPageBreadcrumbs?.(companyBreadcrumbs);

    return () => setPageBreadcrumbs?.(null);
  }, [companyBreadcrumbs, setPageBreadcrumbs, isValuationReadyForAudit]);
};

export default useCompanyBreadcrumb;
